import axios from "axios";
import React, { useState, useEffect } from "react";
import { useOutletContext } from "react-router-dom";
import toastr from "../../../utils/toast";

// Structured default prompts
const promptCategories = [
  {
    category: "Industry & Market",
    icon: "fa-solid fa-arrow-trend-up",
    prompts: [
      {
        id: 1,
        title: "Industry evolution & outlook",
        content: `Create a structured presentation on the [industry] covering:\n– Market size and growth trends over the last 5 years\n– Key drivers of growth and disruption\n– Major challenges, regulatory constraints, and headwinds\n– Shifts in business models, platforms, and customer behavior\n– A forward-looking outlook for the next 3–5 years\nUse charts, timelines, and executive-ready visuals.`,
      },
      {
        id: 2,
        title: "Trends, technology & disruption",
        content: `Build an analytical deck highlighting emerging trends, disruptive technologies, and changing customer expectations in the [industry], and explain their strategic and talent implications.`,
      },
      {
        id: 3,
        title: "Competitive landscape & market structure",
        content: `Create a competitive landscape analysis of the [industry] comparing leading players across scale, capabilities, geographic presence, and differentiation, and summarize how the market structure is evolving.`,
      },
    ],
  },
  {
    category: "Strategy & Consulting",
    icon: "fa-solid fa-brain",
    prompts: [
      {
        id: 4,
        title: "Growth strategy & operating model",
        content: `Create a strategy presentation outlining growth opportunities for the [industry / company] across products, markets, and customer segments, and propose an operating model required to deliver this growth.`,
      },
      {
        id: 5,
        title: "Business challenges & mitigation",
        content: `Analyze the top strategic, operational, and organizational challenges faced by companies in the [industry], and outline practical mitigation approaches and trade-offs.`,
      },
      {
        id: 6,
        title: "Digital, data & AI transformation",
        content: `Build a transformation roadmap for a [traditional / scaling / global] company in the [industry], covering digital, data, and AI capabilities required over the next 2–3 years.`,
      },
    ],
  },
  {
    category: "Executive Search & Talent",
    icon: "fa-solid fa-suitcase",
    prompts: [
      {
        id: 7,
        title: "Role description & success metrics",
        content: `Create a role description for a [job title] in the [industry], covering mandate, key responsibilities, success metrics, critical capabilities, and common failure risks.`,
      },
      {
        id: 8,
        title: "Ideal candidate & talent market",
        content: `Build an ideal candidate profile for a [job title] role in [company], and map the relevant global talent market including target companies, background patterns, and scarcity risks.`,
      },
      {
        id: 9,
        title: "Search strategy & risk assessment",
        content: `Create a detailed search strategy for a [job title] role outlining target geographies, companies, candidate archetypes, key risks, and mitigation actions.`,
      },
      {
        id: 10,
        title: "Interview & assessment framework",
        content: `Design a structured interview and assessment framework to evaluate technical, leadership, and cultural fit for a [job title] role.`,
      },
    ],
  },

  {
    category: "Client-Facing & Delivery",
    icon: "fa-solid fa-users",
    prompts: [
      {
        id: 11,
        title: "Client pitch & 'why now' narrative",
        content: `Create a client-ready pitch deck for a [job title] search that explains the role context, “why this role, why now” narrative, search approach, timelines, and success criteria.`,
      },
      {
        id: 12,
        title: "Search progress & market feedback",
        content: `Draft a clear, executive-friendly client update summarizing search progress, market feedback, candidate quality, risks, and recommended next steps.`,
      },
    ],
  },

  {
    category: "AI, Leadership & Org Design",
    icon: "fa-solid fa-robot",
    prompts: [
      {
        id: 13,
        title: "AI impact & leadership implications",
        content: `Create a presentation explaining how AI is transforming the [industry], including key use cases, risks, regulatory considerations, and implications for leadership roles and skills.`,
      },
      {
        id: 14,
        title: "AI-ready organization & roles",
        content: `Design an AI-ready organization structure for a [start-up / mid-size / enterprise] company, including required leadership roles (CAIO, CDO, CTO), team structures, and governance.`,
      },
    ],
  },

  {
    category: "Executive Utility",
    icon: "fa-solid fa-bolt",
    prompts: [
      {
        id: 15,
        title: "Executive brief & talking points",
        content: `Summarize the [topic / document] into an executive brief with key insights, risks, and talking points for senior stakeholders.`,
      },
      {
        id: 16,
        title: "Slide or content refinement",
        content: `Rewrite this content to be sharper, more structured, and executive-ready while preserving the original intent.`,
      },
    ],
  },
];

export default function AddSearchProposal({ show, onClose }) {
  const [format, setFormat] = useState("Presentation");
  const { token, setGlobalLoading } = useOutletContext();
  const [numCards, setNumCards] = useState(5);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [pageSize] = useState("Traditional 16:9");
  const [expandedId, setExpandedId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  const [themes, setThemes] = useState([]);
  const [themeId, setThemeId] = useState("arajdqig4cawzrc");

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const storyMessages = [
  "🧠 Understanding your idea...",
  "🗂️ Structuring the storyline...",
  "✍️ Writing slide content...",
  "🎨 Designing layouts...",
  "📊 Adding visuals...",
  "✨ Final polish...",
  "🔍 Reviewing your data...",
  "📐 Adjusting margins and spacing...",
  "💡 Brainstorming creative ideas...",
  "📈 Analyzing trends and insights...",
  "🖌️ Crafting unique designs...",
  "⚙️ Fine-tuning the animations...",
  "📜 Generating the perfect outline...",
  "📚 Researching the best examples...",
  "🔄 Checking consistency...",
  "🎬 Preparing a seamless experience...",
  "🎯 Aligning content to your goals...",
  "⚡ Optimizing for impact...",
  "🌟 Adding the finishing touches...",
  "💼 Formatting for professional impact...",
  "🎉 Wrapping up your presentation...",
  "🚀 Ready to launch your idea!",
  "🌐 Reviewing global trends...",
  "📈 Calculating impact...",
  "🚀 Finalizing the layout...",
  "🔗 Connecting the dots...",
  "📅 Preparing the timeline...",
  "🖥️ Polishing the visual details...",
  "📦 Packaging the presentation...",
  "🎤 Perfecting the delivery...",
  "🛠️ Assembling all the pieces...",
  "🎭 Creating a captivating story...",
  "🌱 Adding growth opportunities...",
  "🎤 Tailoring to your audience...",
  "📝 Preparing a summary...",
  "📊 Finalizing charts and graphs...",
  "🎧 Crafting the narrative tone...",
  "⏱️ Speeding up the process...",
  "🔥 Adding high-impact visuals...",
  "🖼️ Picking the perfect imagery...",
  "💬 Fine-tuning messaging...",
  "📹 Embedding multimedia elements...",
  "🔑 Ensuring your presentation is key...",
  "🎨 Selecting the ideal color palette...",
  "🎯 Finalizing key insights...",
  "💬 Adding impactful quotes...",
  "🔒 Securing your presentation...",
  "💼 Wrapping up the professional touches...",
  "⏳ Almost there...",
  "📝 Reviewing and editing...",
  "🌠 Your presentation is almost ready!",
  "🚀 We’re all set to launch!",
  "✨ Making it shine!",
  "🌟 You’re just a few steps away!",
  "🛠️ Almost polished and ready...",
  "🔧 Final adjustments underway...",
  "🎁 Wrapping it all up...",
  "🔒 Checking everything is secure...",
  "📝 Perfecting your final draft...",
  "💫 And... we’re done!",
  "⚡ Presentation is ready to go!"
];


  const [storyIndex, setStoryIndex] = useState(0);
  const [isGenerating, setIsGenerating] = useState(false);

  useEffect(() => {
    const fetchThemes = async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/gamma/themes`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setThemes(res.data.themes || []);
      } catch (err) {
        console.error("Failed to load themes", err);
      }
    };
    fetchThemes();
  }, [API_BASE_URL, token]);
  useEffect(() => {
    if (!isGenerating) return;

    const interval = setInterval(() => {
      setStoryIndex((prev) => (prev + 1) % storyMessages.length);
    }, 2200);

    return () => clearInterval(interval);
  }, [isGenerating]);

  if (!show) return null;

  // ===== LOGIC UNCHANGED =====
  const handleGenerate = async () => {
    if (!title.trim()) {
      toastr.error("Please enter a title.");
      return;
    }
    if (!content.trim()) {
      toastr.error("Please enter content to generate.");
      return;
    }

    try {
      setIsGenerating(true);
      //setGlobalLoading(true);
      const res = await axios.post(
        `${API_BASE_URL}/gamma/generate`,
        { title, inputText: content, numCards, format, pageSize, themeId },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const generationId = res.data?.generationId;
      if (!generationId) throw new Error("No generation ID");

      pollGenerationStatus(generationId);
    } catch (err) {
      setIsGenerating(false);
      // setGlobalLoading(false);
      toastr.error("Failed to start generation");
    }
  };

  const pollGenerationStatus = async (generationId) => {
    const interval = setInterval(async () => {
      try {
        const res = await axios.get(
          `${API_BASE_URL}/gamma/status/${generationId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (res.data?.status === "completed") {
          clearInterval(interval);
          setIsGenerating(false);
          //setGlobalLoading(false);
          window.open(res.data.gammaUrl, "_blank");
          onClose();
          window.location.reload()
        }

        if (res.data?.status === "failed") {
          clearInterval(interval);
          setIsGenerating(false);
          //setGlobalLoading(false);
          toastr.error("Gamma generation failed");
        }
      } catch {
        clearInterval(interval);
        setIsGenerating(false);
        //setGlobalLoading(false);
      }
    }, 5000);
  };

  const toggleCard = (id) => setExpandedId(expandedId === id ? null : id);

  const filteredPrompts = promptCategories
    .map((category) => ({
      ...category,
      prompts: category.prompts.filter(
        (p) =>
          p.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
          p.id.toString() === searchTerm
      ),
    }))
    .filter((category) => category.prompts.length > 0);

  const insertPrompt = (p) => {
    setTitle(p.title);
    setContent(p.content);
  };

  // ===== UI =====
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.4)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999,
      }}
    >
      <div
        style={{
          width: "92%",
          maxWidth: 1200,
          height: "90vh",
          background: "#fff",
          borderRadius: 20,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          fontFamily: "Segoe UI, sans-serif",
          position: "relative",
        }}
      >
        {/* ===== HEADER ===== */}
        <div
          style={{
            padding: 24,
            background: "linear-gradient(135deg, #f26522)",
            color: "#fff",
            position: "relative",
          }}
        >
          <button
            onClick={onClose}
            style={{
              position: "absolute",
              right: 20,
              top: 20,
              background: "transparent",
              border: "none",
              fontSize: 22,
              color: "#fff",
              cursor: "pointer",
            }}
          >
            ✕
          </button>

          <div style={{ display: "flex", gap: 12 }}>
            <div
              style={{
                width: 42,
                height: 42,
                borderRadius: 12,
                background: "rgba(255,255,255,0.25)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 20,
              }}
            >
              ✨
            </div>
            <div>
              <h3 style={{ margin: 0 }}>Create with AI</h3>
              <p style={{ margin: "4px 0 0", opacity: 0.9 }}>
                Describe your presentation topic and let AI create stunning
                slides for you.
              </p>
            </div>
          </div>
        </div>

        {/* ===== BODY ===== */}
        <div
          style={{
            flex: 1,
            display: "flex",
            gap: 20,
            padding: 20,
            overflow: "hidden",
            background: "#f9fafb",
          }}
        >
          {/* LEFT PROMPTS */}
          <div
            style={{
              flex: 1,
              background: "#eef5ff",
              borderRadius: 16,
              padding: 16,
              overflowY: "auto",
            }}
          >
            <h5>Quick Prompts</h5>
            <input
              className="form-control mb-3"
              placeholder="Search prompts..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />

            {filteredPrompts.map((category) => (
              <div key={category.category}>
                <div
                  style={{
                    marginTop: "20px",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    fontSize: "12px",
                    fontWeight: 500,
                    letterSpacing: "0.08em",
                    color: "#6b7280", // soft gray
                    textTransform: "uppercase",
                  }}
                >
                  {category.icon && (
                    <i
                      className={category.icon}
                      style={{ fontSize: "14px", opacity: 0.8 }}
                    />
                  )}
                  {category.category}
                </div>

                {category.prompts.map((p) => (
                  <div
                    key={p.id}
                    onClick={() => toggleCard(p.id)}
                    style={{
                      background: "#fff",
                      borderRadius: 12,
                      padding: 12,
                      marginBottom: 10,
                      cursor: "pointer",
                      border:
                        expandedId === p.id
                          ? "2px solid #f26522"
                          : "1px solid #ddd",
                    }}
                  >
                    <strong>
                      {p.id}. {p.title}
                    </strong>

                    {expandedId === p.id && (
                      <>
                        <div style={{ marginTop: 8, fontSize: 14 }}>
                          {p.content.split("\n").map((l, i) => (
                            <p key={i} style={{ margin: 0 }}>
                              {l}
                            </p>
                          ))}
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            insertPrompt(p);
                          }}
                          style={{
                            marginTop: 10,
                            background: "#f26522",
                            color: "#fff",
                            border: "none",
                            borderRadius: 8,
                            padding: "6px 12px",
                            cursor: "pointer",
                          }}
                        >
                          Use this prompt
                        </button>
                      </>
                    )}
                  </div>
                ))}
              </div>
            ))}
          </div>

          {/* RIGHT CONTROLS */}
          <div
            style={{
              flex: 1.5,
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* FORMAT */}

            {/* TITLE */}
            <div className="mb-3">
              <label className="form-label fw-semibold">Title *</label>
              <input
                className="form-control"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </div>

            {/* SLIDES + THEME */}
            <div className="d-flex gap-3 mb-3">
              <div className="flex-fill">
                <label className="form-label">No. of Slides</label>
                <input
                  type="number"
                  className="form-control"
                  min={1}
                  value={numCards}
                  onChange={(e) => setNumCards(Number(e.target.value))}
                />
              </div>

              <div className="flex-fill">
                <label
                  className="form-label"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    fontWeight: 500,
                  }}
                >
                  <i
                    className="fa-solid fa-palette"
                    style={{ fontSize: "14px", opacity: 0.8 }}
                  />
                  Theme
                </label>
                <select
                  className="form-select"
                  value={themeId}
                  onChange={(e) => setThemeId(e.target.value)}
                >
                  {themes.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* CONTENT */}
            <div
              style={{
                flex: 1,
                background: "#fff",
                borderRadius: 16,
                padding: 16,
              }}
            >
              <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="Select a template or write your own prompt..."
                style={{
                  width: "100%",
                  height: "100%",
                  border: "none",
                  outline: "none",
                  resize: "none",
                  fontSize: 15,
                }}
              />
            </div>

            {/* GENERATE */}
            <button
              onClick={handleGenerate}
              style={{
                marginTop: 20,
                height: 50,
                borderRadius: 26,
                border: "none",
                fontSize: 16,
                fontWeight: 700,
                color: "#fff",
                cursor: "pointer",
                background: "linear-gradient(135deg, #f26522, #ff8a4c)",
              }}
            >
              ⚡ Generate Presentation
            </button>
          </div>
        </div>
      </div>
     {isGenerating && (
  <div
    style={{
      position: "fixed",
      inset: 0,
      background: "rgba(0, 0, 0, 0.8)", // Dark overlay to cover the entire screen
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 9999,
      flexDirection: "column",
    }}
  >
    <div
      className="fade-in"
      style={{
        fontSize: "36px",
        fontWeight: 600,
        color: "#fff",
        textAlign: "center",
      }}
    >
      {storyMessages[storyIndex]} {/* Show the current message */}
    </div>
    <div
      style={{
        marginTop: 10,
        fontSize: "18px",
        color: "#fff", // Text color for the secondary line
        opacity: 0.7,
      }}
    >
      Creating your presentation…
    </div>
  </div>
)}

    </div>
  );
}
